import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { MapPin, CalendarDays, Trophy, Users, FileText, Search, ArrowDown } from "lucide-react";
import api, { API } from "@/lib/api";
import { brandOf, money, FusionMeter, Countdown, ShareControls, textOn, visibleOnDark } from "@/components/fusion/FusionShared";

export default function FusionTeamPage() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("fundraising");
  const [query, setQuery] = useState("");

  useEffect(() => {
    api.get(`/fusion/public/team/${slug}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [slug]);

  if (err) return <div className="min-h-screen bg-[#080e1c] text-white/60 text-center pt-24">Campaign not found.</div>;
  if (!data) return <div className="min-h-screen bg-[#080e1c] text-white/40 text-center pt-24 animate-pulse">Loading campaign…</div>;

  const { event, totals, athletes, recent_activity } = data;
  const b = brandOf(event);
  const url = window.location.href;
  const safeSecondary = visibleOnDark(b.secondary);
  const safeAccent = visibleOnDark(b.accent);
  const perf = [...athletes].filter((a) => a.result_verified).sort((x, y) => (y.best_distance_ft || 0) - (x.best_distance_ft || 0));
  const base = tab === "performance" ? perf : athletes;
  const list = query.trim()
    ? base.filter((a) => (a.athlete_name || "").toLowerCase().includes(query.trim().toLowerCase()))
    : base;
  const scrollToPlayers = () => document.getElementById("athletes")?.scrollIntoView({ behavior: "smooth" });

  return (
    <div className="min-h-screen pb-20" style={{ background: `radial-gradient(1200px 500px at 50% -100px, ${b.primary}66, transparent), #080e1c` }}>
      {/* Hero banner photo */}
      {b.teamPhoto && (
        <div className="relative w-full h-56 sm:h-80 overflow-hidden" data-testid="team-hero-banner">
          <img src={b.teamPhoto} alt={b.school} className="absolute inset-0 w-full h-full object-cover" />
          <div className="absolute inset-0" style={{ background: `linear-gradient(180deg, rgba(8,14,28,0.15) 0%, rgba(8,14,28,0.55) 60%, #080e1c 100%)` }} />
          <div className="absolute bottom-4 left-0 right-0 max-w-5xl mx-auto px-5 flex items-end gap-4">
            {b.logo && <img src={b.logo} alt={b.school} className="h-16 w-16 object-contain rounded-xl bg-white/90 p-1.5 shadow-lg" />}
            <div>
              <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: b.secondary }}>BioHacker Fusion™ Team Campaign</div>
              <div className="text-3xl sm:text-4xl font-black text-white drop-shadow">{b.school}</div>
            </div>
          </div>
        </div>
      )}
      <div className="max-w-5xl mx-auto px-5">
        {/* Hero */}
        <motion.header initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className={`${b.teamPhoto ? "pt-6" : "pt-10"} flex flex-col sm:flex-row gap-6 items-start sm:items-center`}>
          {!b.teamPhoto && b.logo && <img src={b.logo} alt={b.school} className="h-20 w-20 object-contain rounded-2xl bg-white/90 p-2" data-testid="team-logo" />}
          <div className="flex-1">
            {!b.teamPhoto && (
              <>
                <div className="text-[10px] uppercase tracking-[0.3em] font-bold" style={{ color: b.secondary }}>BioHacker Fusion™ Team Campaign</div>
                <h1 className="text-4xl sm:text-5xl font-black text-white mt-1" data-testid="team-campaign-title">{b.school}</h1>
              </>
            )}
            {b.teamPhoto && <h1 className="sr-only" data-testid="team-campaign-title">{b.school}</h1>}
            <div className="text-sm text-white/60 mt-1">{event.team_name} · {event.name}</div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-white/60">
              {event.event_date && <span className="inline-flex items-center gap-1.5"><CalendarDays className="h-4 w-4" style={{ color: b.secondary }} />{new Date(event.event_date).toLocaleDateString(undefined, { dateStyle: "long" })}</span>}
              {event.location && <span className="inline-flex items-center gap-1.5"><MapPin className="h-4 w-4" style={{ color: b.secondary }} />{event.location}</span>}
              <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" style={{ color: b.secondary }} />{totals.athlete_count} athletes · {totals.sponsor_count} sponsors</span>
            </div>
          </div>
          <Countdown dateStr={event.event_date} color={safeSecondary} />
        </motion.header>

        {/* Team meter */}
        <section className="mt-8 rounded-3xl border border-white/10 p-6 sm:p-8" style={{ background: `linear-gradient(135deg, ${b.primary}dd 0%, rgba(10,18,36,0.95) 70%)` }}>
          <FusionMeter raised={totals.raised_usd} goal={totals.team_goal_usd} color={b.secondary} testId="team-campaign-meter" />
          {event.coach_message && (
            <div className="mt-5 flex gap-3 items-start">
              {event.branding?.coach_photo_url && <img src={event.branding.coach_photo_url} alt="Coach" className="h-10 w-10 rounded-full object-cover" />}
              <p className="text-sm text-white/75 italic" data-testid="coach-message">"{event.coach_message}"</p>
            </div>
          )}
          {event.fundraising_message && <p className="mt-3 text-sm text-white/60">{event.fundraising_message}</p>}
          <div className="mt-5 flex flex-wrap items-center gap-3">
            <button onClick={scrollToPlayers} data-testid="find-player-btn"
              className="inline-flex items-center gap-1.5 px-5 py-2.5 rounded-full text-sm font-black transition-transform hover:scale-[1.03]"
              style={{ background: safeSecondary, color: textOn(safeSecondary) }}>
              <ArrowDown className="h-4 w-4" /> Find a player to support
            </button>
            <ShareControls url={url} title={`Support ${b.school} — BioHacker Fusion Combine & Hit-A-Thon`} color={b.secondary} />
            {event.has_flyer && (
              <a href={`${API}/fusion/flyer/${event.event_id}`} target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold border text-white transition-colors hover:bg-white/10"
                style={{ borderColor: safeAccent, color: safeAccent }} data-testid="view-flyer-btn">
                <FileText className="h-3.5 w-3.5" /> View fundraiser flyer
              </a>
            )}
          </div>
        </section>

        {/* What is Fusion */}
        <section className="mt-6 rounded-2xl border border-white/10 bg-white/5 p-6 text-sm text-white/65">
          <span className="font-black text-white">This is not cookie-dough fundraising.</span> BioHacker Fusion™ is a performance combine + fundraiser:
          athletes take five official swings, sponsors pledge per foot of their best verified distance (or donate flat), and every verified result feeds the
          athlete's permanent BioHacker performance profile.
        </section>

        {/* Leaderboard tabs */}
        <section className="mt-8 scroll-mt-6" id="athletes">
          <h2 className="text-2xl font-black text-white" data-testid="support-a-player-heading">Support a Player</h2>
          <p className="text-sm text-white/50 mt-1 mb-4">Tap any player card to open their personal fundraiser page and make a pledge.</p>
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <button onClick={() => setTab("fundraising")} data-testid="tab-fundraising"
              className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider border ${tab === "fundraising" ? "" : "text-white/60 border-white/15"}`}
              style={tab === "fundraising" ? { background: safeSecondary, borderColor: safeSecondary, color: textOn(safeSecondary) } : {}}>
              Fundraising leaderboard
            </button>
            <button onClick={() => setTab("performance")} data-testid="tab-performance"
              className={`px-4 py-2 rounded-full text-xs font-black uppercase tracking-wider border ${tab === "performance" ? "" : "text-white/60 border-white/15"}`}
              style={tab === "performance" ? { background: safeSecondary, borderColor: safeSecondary, color: textOn(safeSecondary) } : {}}>
              Performance leaderboard
            </button>
            {athletes.length > 3 && (
              <div className="relative ml-auto">
                <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
                <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Find a player…"
                  className="pl-9 pr-3 py-2 rounded-full text-sm bg-white/5 border border-white/15 text-white placeholder:text-white/35 outline-none focus:border-white/40 w-48"
                  data-testid="player-search-input" />
              </div>
            )}
          </div>

          {athletes.length === 0 && (
            <div className="rounded-2xl border border-dashed border-white/15 p-10 text-center text-white/50 text-sm" data-testid="no-athletes-state">
              Player fundraiser pages are on the way — athletes are creating their accounts now. Check back soon, or share this page so supporters are ready!
            </div>
          )}
          {athletes.length > 0 && list.length === 0 && (
            <div className="text-sm text-white/40 py-8 text-center" data-testid="no-search-results">
              {tab === "performance" && !query.trim()
                ? "Performance results appear after the Fusion Event is verified."
                : `No players match "${query}".`}
            </div>
          )}

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="athlete-cards-grid">
            {list.map((a, i) => (
              <motion.div key={a.campaign_id} initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: Math.min(i * 0.05, 0.5) }}>
                <Link to={`/fusion/a/${a.slug}`} data-testid={`athlete-card-${a.slug}`}
                  className="block rounded-2xl border border-white/10 bg-white/5 p-5 hover:bg-white/[0.08] hover:border-white/20 transition-colors group">
                  <div className="flex items-center gap-3">
                    {a.photo_url ? (
                      <img src={a.photo_url} alt={a.athlete_name} className="h-14 w-14 rounded-xl object-cover border" style={{ borderColor: safeSecondary }} />
                    ) : (
                      <div className="h-14 w-14 rounded-xl bg-white/10 flex items-center justify-center font-black text-white/40">{a.athlete_first?.[0]}</div>
                    )}
                    <div className="min-w-0">
                      <div className="font-black text-white truncate">{a.athlete_name}</div>
                      <div className="text-[11px] text-white/50">{a.position || "Athlete"} · {a.sponsor_count} sponsors</div>
                    </div>
                    <div className="ml-auto text-lg font-black" style={{ color: safeSecondary }}>#{tab === "performance" ? i + 1 : a.fundraising_rank}</div>
                  </div>
                  <div className="mt-3">
                    <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                      <div className="h-full rounded-full" style={{ background: safeSecondary, width: `${Math.min(100, (a.raised_usd / (a.goal_usd || 1)) * 100)}%` }} />
                    </div>
                    <div className="mt-1.5 flex justify-between text-[11px]">
                      <span className="text-white font-bold">{money(a.raised_usd)}</span>
                      <span className="text-white/40">of {money(a.goal_usd)}</span>
                    </div>
                  </div>
                  {a.result_verified && (
                    <div className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold" style={{ color: safeAccent }}>
                      <Trophy className="h-3.5 w-3.5" /> Best hit: {a.best_distance_ft?.toFixed(0)} ft
                    </div>
                  )}
                  <div className="mt-3 text-[11px] font-black uppercase tracking-wider opacity-70 group-hover:opacity-100" style={{ color: safeSecondary }}>
                    Sponsor {a.athlete_first} →
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        </section>

        {/* Recent activity */}
        {recent_activity?.length > 0 && (
          <section className="mt-8 rounded-2xl border border-white/10 bg-white/5 p-6" data-testid="team-recent-activity">
            <div className="text-[10px] uppercase tracking-[0.25em] font-bold text-white/40 mb-3">Recent contributions</div>
            <div className="grid sm:grid-cols-2 gap-2">
              {recent_activity.map((s, i) => (
                <div key={i} className="flex items-center justify-between text-sm bg-white/[0.03] rounded-lg px-3 py-2">
                  <span className="text-white/80 font-semibold">{s.sponsor} <span className="text-white/40 font-normal">→ {s.athlete}</span></span>
                  <span className="text-white/50 text-xs">{s.type === "flat" ? (s.amount_usd ? money(s.amount_usd) : "Donation") : `$${(s.rate_usd_per_ft || 0).toFixed(2)}/ft`}</span>
                </div>
              ))}
            </div>
          </section>
        )}

        <div className="mt-10 text-center text-[10px] uppercase tracking-[0.3em] text-white/30">
          BioHacker Fusion™ × {b.school} · Raise. Measure. Develop. Repeat.™
        </div>
      </div>
    </div>
  );
}
